import EzGoLogo from '@luna-protocol/core/src/assets/ezgo-promo.svg';
import AquaLogo from '@luna-protocol/core/src/assets/formLogo.png';
import Button from '@luna-protocol/core/src/components/Button/Button';
import LineBreak from '@luna-protocol/core/src/components/LineBreak/LineBreak';
import { USDollar } from '@luna-protocol/core/src/utils/constants/format.ts';
import useToast from '@luna-protocol/core/src/utils/useToast.ts';
import { toastStore } from '@chakra-ui/react';
import { useEffect, useState, useRef } from 'react';
import { useIntl } from 'react-intl';
import { OEMType as EOEMType } from '@luna-protocol/core/src/types.ts';
import { SerialNumber } from '../../types.ts';
import { Form, Formik, FormikHelpers } from 'formik';
import config from '@luna-protocol/core/src/utils/feature-flags.json';
import { ProductItem } from '../../types';
import { useGetMakeModels } from '../../queries/useGetMakeModels.ts';
import OfferCard from './OfferCard.tsx';
import messages from './ProductSummary.messages.ts';
import './ProductSummary.scss';
import { buildProductList } from '../../utils/buildProductList.ts';
import { returnImgSrc } from '@luna-protocol/core/src/utils/returnImgSrc.ts';
import ProductFormFields from './ProductFormFields.tsx';
import { useAppStore } from '../../useAppStore.ts';

export interface ProductSummaryProps {
  AddOnTotal?: number;
  editable?: boolean;
  approvedAmount?: number;
  showConfirmButton?: boolean;
  onSubmit?: (products: ProductItem[][]) => void;
  ButtonText?: string;
  showFinancing?: boolean;
  refetchTrigger?: boolean;
  setRefetchTrigger?: React.Dispatch<React.SetStateAction<boolean | false>>;
}

type FormValues = { serialNumbers: SerialNumber[] };
type SetFieldValueType = FormikHelpers<FormValues>['setFieldValue'];

const ProductSummary = ({
  approvedAmount,
  AddOnTotal,
  showConfirmButton = true,
  editable = true,
  ButtonText,
  onSubmit,
  showFinancing = true,
  refetchTrigger,
  setRefetchTrigger,
}: ProductSummaryProps) => {
  const OEMType = useAppStore(state => state.OEMType);
  const serialNumbers = useAppStore(state => state.serialNumbers);
  const dealerFlowTemplate = useAppStore(state => state.dealerFlowTemplate);
  const { formatMessage } = useIntl();
  const initialTotal = AddOnTotal ? AddOnTotal + 0 : 0;
  const [runningTotal, setRunningTotal] = useState(initialTotal);
  const [productList, setProductList] = useState<ProductItem[][] | undefined>(undefined);
  const [selectedMake, setSelectedMake] = useState('');
  const [selectedModel, setSelectedModel] = useState('');
  const [disableButton, setDisableButton] = useState(false);
  const [isEditable, setIsEditable] = useState(false);

  const normalizeAssetClassStr = (assetClass?: string) => {
    if (!assetClass) return;

    if (assetClass.includes('ATV') || assetClass.includes('UTV')) {
      return assetClass.toUpperCase();
    }

    return assetClass
      .split(' ')
      .map((word: string) => `${word.charAt(0).toUpperCase()}${word.slice(1).toLowerCase()}`)
      .join(' ');
  };

  const [mainAssetClass, setMainAssetClass] = useState(
    normalizeAssetClassStr(serialNumbers?.serial_numbers[0].serial_number_asset_class),
  );

  const { createToast } = useToast();
  const toastPro = toastStore;
  const prevAssetClass = useRef<string>('');
  const prevMake = useRef<string>('');
  const prevModel = useRef<string>('');
  const { refetch: makeModelsRefetch } = useGetMakeModels(mainAssetClass);

  const flattenProductList = (list: ProductItem[][] | undefined): ProductItem[] => (list ? list.flat() : []);

  // Product Summary Utility Functions
  const getInvoiceTitle = () => {
    return config.FF_OEMTemplatesEnabled
      ? `${dealerFlowTemplate?.seller_purchase_price_type}/NADA`
      : OEMType === EOEMType.KTM
        ? formatMessage(messages.productInformation.fields.colour.ktm)
        : OEMType === EOEMType.EZGO
          ? formatMessage(messages.productInformation.fields.colour.ezgo)
          : formatMessage(messages.productInformation.fields.colour.brunswick);
  };

  const replaceTitleIfInvoice = (product: ProductItem) => {
    if (product.title.toLowerCase().includes('NADA/MSRP Invoice'.toLowerCase())) {
      return (product.title = `${product.title.split(' ')[0]} ${getInvoiceTitle()}`);
    }

    return product.title;
  };

  function removeUnitPrefix(title: string, serialNumberType: string, searchIndex: number) {
    const prefix = getUnitPrefix(serialNumberType, searchIndex);
    if (prefix && title.startsWith(`${prefix} `)) {
      return title.slice(prefix.length + 1);
    }
    return title;
  }

  const getUnitPrefix = (serialNumberType: string | undefined, replaceIndex: number) => {
    const safeType = serialNumberType || '';
    const testType = safeType.replace(new RegExp(`_${replaceIndex}$`), '');
    switch (testType) {
      case 'hull-id':
        return `HIN`;
      case 'engine':
        return `Engine`;
      case 'trailer':
        return `Trailer`;
      case '':
        return ``;
      default:
        return `VIN`;
    }
  };

  function productListToSerialNumbers(productList: ProductItem[][]): SerialNumber[] {
    return productList.map((products, index) => {
      const getValue = (key: string) =>
        products.find(item => item.title.replace(/\s|_/g, '').toLowerCase() === key.replace(/\s|_/g, '').toLowerCase())
          ?.value;

      const getString = (key: string) => {
        const val = getValue(key);
        if (typeof val === 'string') return val;
        if (typeof val === 'number') return String(val);
        return '';
      };

      const getNumber = (key: string) => {
        const val = getValue(key);
        if (typeof val === 'number') return val;
        if (typeof val === 'string') {
          const num = Number(val);
          return isNaN(num) ? 0 : num;
        }
        return 0;
      };

      const serialNumberType = products[2].type ?? '';
      const unitPrefix = getUnitPrefix(serialNumberType, index);

      return {
        collateral_id: getString('collateral_id'),
        make: getString(`${unitPrefix} Make`),
        model: getString(`${unitPrefix} Model`),
        color: getString(`${unitPrefix} Color`),
        serial_number: getString(`${unitPrefix} Number`),
        serial_number_type: serialNumberType,
        serial_number_asset_class: getString(`${unitPrefix} Asset Class`),
        product_price: getNumber(`${unitPrefix} Price`),
        condition: getString(`${unitPrefix} Condition`),
        seller_purchase_price: getNumber('seller_purchase_price'),
        year: getNumber(`${unitPrefix} Year`),
        nada_msrp_invoice: getNumber(`${unitPrefix} NADA/MSRP Invoice`),
      };
    });
  }

  function serialNumberToProductList(serial: SerialNumber, valueType: string, index: number): ProductItem[] {
    const unitPrefix = getUnitPrefix(valueType, index);
    return [
      { title: 'Collateral ID', value: serial.collateral_id, type: valueType },
      { title: `${unitPrefix} Make`, value: serial.make, type: valueType },
      { title: `${unitPrefix} Model`, value: serial.model, type: valueType },
      { title: `${unitPrefix} Color`, value: serial.color, type: valueType },
      { title: `${unitPrefix} Number`, value: serial.serial_number, type: valueType },
      { title: `${unitPrefix} Type`, value: serial.serial_number_type, type: valueType },
      { title: `${unitPrefix} Asset Class`, value: serial.serial_number_asset_class, type: valueType },
      { title: `${unitPrefix} Price`, value: serial.product_price, type: valueType },
      { title: `${unitPrefix} Condition`, value: serial.condition, type: valueType },
      { title: 'Seller Purchase Price', value: serial.seller_purchase_price, type: valueType },
      { title: `${unitPrefix} Year`, value: serial.year, type: valueType },
      { title: `${unitPrefix} NADA/MSRP Invoice`, value: serial.nada_msrp_invoice, type: valueType },
    ];
  }

  const getErrorFieldMessage = (product: ProductItem, index: number) => {
    product.title = replaceTitleIfInvoice(product);
    if (index > 0) {
      if (product.type === 'engine_3') {
        return `${product.title
          .split(' ')
          .flatMap((word, i) => (i === 0 ? [word, '2'] : [word]))
          .join(' ')}`;
      } else {
        return product.title;
      }
    } else {
      return removeUnitPrefix(product.title, product.type || '', index);
    }
  };

  // Handler for Required Fields Check

  const allRequiredFieldsFilled = (updatedProductList?: ProductItem[][]) => {
    if (!productList && !updatedProductList) return;
    if (!updatedProductList) {
      updatedProductList = productList;
    }
    const nonRequiredTitles = ['Color'];
    const invalidIfZeroTitles = ['NADA/MSRP Invoice', ' Price', 'Year'];

    const isNonRequired = (title: string) =>
      nonRequiredTitles.some(nonReq => title.toLowerCase().includes(nonReq.toLowerCase()));

    const isZeroInvalid = (title: string) =>
      invalidIfZeroTitles.some(
        zeroField => title.toLowerCase().includes(zeroField.toLowerCase()) && title !== 'Seller Purchase Price',
      );

    const missingRequired: { product: ProductItem; index: number; reason: string }[] = [];

    updatedProductList?.forEach((productArr, index) => {
      productArr.forEach(product => {
        if (isNonRequired(product.title)) return;

        const value = typeof product.value === 'string' ? product.value.trim() : product.value;

        const isEmpty = value === undefined || value === null || value === '';
        const isInvalidZero = Number(value) <= 0 && isZeroInvalid(product.title);

        if (isEmpty) {
          missingRequired.push({ product, index, reason: 'empty' });
        } else if (isInvalidZero) {
          missingRequired.push({ product, index, reason: 'zero' });
        }
      });
    });

    if (missingRequired.length > 0) {
      missingRequired.forEach(({ product, index, reason }) => {
        const toastID = `${replaceTitleIfInvoice(product)}-${product.type}`;
        if (!toastPro.isActive(toastID)) {
          createToast({
            title: formatMessage(messages.missingValueToastError, {
              errorField: getErrorFieldMessage(product, index),
              value: reason === 'zero' ? '0' : reason,
            }),
            status: 'error',
            isClosable: true,
            duration: null,
            id: toastID,
          });
        }
      });

      setDisableButton(true);
      return false;
    } else {
      setDisableButton(false);
      return true;
    }
  };

  // Handler Functions for Associating Asset Class/Makes/Models
  const handleAssetClassChange = (newValue: string, setFieldValue: SetFieldValueType, index: number) => {
    setFieldValue(`serialNumbers[${index}].serial_number_asset_class`, newValue);
    handleMakeChange('', setFieldValue, index);

    if (index === 0 && newValue !== mainAssetClass) {
      setMainAssetClass(newValue);
      makeModelsRefetch();
    }
  };

  const handleMakeChange = (newValue: string, setFieldValue: SetFieldValueType, index?: number) => {
    if (typeof index === 'number') {
      setFieldValue(`serialNumbers[${index}].make`, newValue);
      setSelectedMake(newValue);
      handleModelChange('', setFieldValue, index);
    }
  };

  const handleModelChange = (newValue: string, setFieldValue: SetFieldValueType, index?: number) => {
    if (typeof index === 'number') {
      setFieldValue(`serialNumbers[${index}].model`, newValue);
      setFieldValue(`serialNumbers[${index}].year`, '');
      setSelectedModel(newValue);
    }
  };

  const getUpdateProductList = (values: FormValues) => {
    return values.serialNumbers.map((serial, index) => {
      const productArr = serialNumberToProductList(serial, serial.serial_number_type, index);
      if (index === 0) {
        const itemValue = [serial.make, serial.model].filter(Boolean).join(' ');
        const itemIndex = productArr.findIndex(p => p.title === 'Item');
        if (itemIndex !== -1) {
          delete productArr[itemIndex].type;
          productArr[itemIndex].value = itemValue;
        } else {
          productArr.unshift({ title: 'Item', value: itemValue });
        }
        for (let i = 0; i < productArr.length; i++) {
          if (productArr[i].title !== 'Item' && !('type' in productArr[i])) {
            productArr[i].type = serial.serial_number_type || '';
          }
        }
      }
      return productArr;
    });
  };

  // useEffect Calls

  useEffect(() => {
    const builtProductList = buildProductList(serialNumbers, OEMType, dealerFlowTemplate?.base_type);
    allRequiredFieldsFilled(builtProductList);
    if (productList === undefined && serialNumbers !== undefined) {
      setProductList(builtProductList);
    } else if (refetchTrigger && setRefetchTrigger) {
      setProductList(builtProductList);
      setRefetchTrigger(false);
    }
  }, [refetchTrigger, serialNumbers, OEMType, setProductList, dealerFlowTemplate?.base_type]);

  useEffect(() => {
    if (!productList || !productList.length) return;
    const firstSerial = productListToSerialNumbers(productList)[0];
    const assetClassValue = firstSerial.serial_number_asset_class;
    setMainAssetClass(assetClassValue);
    makeModelsRefetch();
  }, []);

  useEffect(() => {
    makeModelsRefetch();
  }, [mainAssetClass]);

  useEffect(() => {
    if (productList === undefined) return;
    const sumPrices = (productList: ProductItem[][]) => {
      const targetItems = flattenProductList(productList).filter(product => product.title.includes('Price'));
      const result = targetItems.reduce((acc: number, obj: ProductItem) => {
        return acc + Number(obj.value);
      }, 0);
      return result;
    };
    const sumTotal = sumPrices(productList);
    setRunningTotal(sumTotal);
  }, [productList]);

  useEffect(() => {
    if (prevAssetClass.current !== mainAssetClass) {
      setSelectedMake('');
      setSelectedModel('');
      prevAssetClass.current = mainAssetClass as string;
      makeModelsRefetch();
    }
    if (prevMake.current !== selectedMake) {
      setSelectedModel('');
      prevMake.current = selectedMake;
    }
    if (prevModel.current !== selectedModel) {
      prevModel.current = selectedModel;
    }
  }, [mainAssetClass, selectedMake, selectedModel, makeModelsRefetch]);

  if (!productList) {
    return;
  }

  return (
    <div className="product-summary">
      <h3>{formatMessage(messages.title)}</h3>
      <div className="product-summary--container">
        <Formik<FormValues>
          key={isEditable ? 'edit' : 'view'}
          initialValues={{ serialNumbers: productListToSerialNumbers(productList) }}
          enableReinitialize
          validateOnMount
          onSubmit={values => {
            const updatedProductList = getUpdateProductList(values);
            setProductList(updatedProductList);
            setIsEditable(false);
            setDisableButton(false);
          }}>
          {({ values, isValid, handleBlur, handleChange, setFieldValue, handleSubmit, submitForm }) => {
            return (
              <Form onSubmit={handleSubmit}>
                <fieldset className="fieldset">
                  {values.serialNumbers.map((serial, index) => {
                    if (!serial) return null;
                    const unitPrefix = getUnitPrefix(serial.serial_number_type, index);
                    const FIELD_ORDER = [
                      `Item`,
                      `${unitPrefix} Asset Class`,
                      `${unitPrefix} Make`,
                      `${unitPrefix} Model`,
                      `${unitPrefix} Year`,
                      `${unitPrefix} Condition`,
                      `${unitPrefix} Color`,
                      `${unitPrefix} Number`,
                      `${unitPrefix} Type`,
                      `${unitPrefix} Price`,
                      'Seller Purchase Price',
                      `${unitPrefix} NADA/MSRP Invoice`,
                    ];
                    const currProductArr = serialNumberToProductList(serial, serial.serial_number_type, index);

                    if (index === 0) {
                      const itemValue = [serial.make, serial.model].filter(Boolean).join(' ');
                      const itemIndex = currProductArr.findIndex(p => p.title === 'Item');
                      if (itemIndex !== -1) {
                        delete currProductArr[itemIndex].type;
                        currProductArr[itemIndex].value = itemValue;
                      } else {
                        currProductArr.unshift({ title: 'Item', value: itemValue });
                      }
                      for (let i = 0; i < currProductArr.length; i++) {
                        if (currProductArr[i].title !== 'Item' && !('type' in currProductArr[i])) {
                          currProductArr[i].type = serial.serial_number_type || '';
                        }
                      }
                    }

                    return (
                      <div key={index} className="product-summary--serial-group">
                        {FIELD_ORDER.map((fieldTitle, fieldIdx) => {
                          const item = currProductArr.find(p => p.title === fieldTitle);

                          if (!item) return null;
                          return (
                            <ProductFormFields
                              key={fieldTitle}
                              item={item}
                              editable={editable}
                              isEditable={isEditable}
                              unitPrefix={unitPrefix}
                              fieldIdx={fieldIdx}
                              serial={serial}
                              setFieldValue={setFieldValue}
                              index={index}
                              handleAssetClassChange={handleAssetClassChange}
                              handleMakeChange={handleMakeChange}
                              handleModelChange={handleModelChange}
                              handleChange={handleChange}
                              handleBlur={handleBlur}
                              getInvoiceTitle={getInvoiceTitle}
                            />
                          );
                        })}
                        <LineBreak bordered />
                      </div>
                    );
                  })}
                </fieldset>
                <div className="product-summary--row">
                  <div className="product-summary--label">{formatMessage(messages.total)}</div>
                  <div className="product-summary--item">
                    <div className="product-summary--total">
                      <p>{USDollar.format(runningTotal)}</p>
                    </div>
                  </div>
                </div>
                <div className="product-summary--button-row">
                  {editable ? (
                    isEditable ? (
                      <>
                        <Button
                          type="button"
                          variant="secondary"
                          fullWidth={false}
                          onClick={event => {
                            event.preventDefault();
                            allRequiredFieldsFilled();
                            setIsEditable(false);
                          }}>
                          Cancel
                        </Button>
                        <Button
                          type="button"
                          disabled={!isValid}
                          variant="secondary"
                          fullWidth={false}
                          onClick={event => {
                            event.preventDefault();
                            const updatedProductList = getUpdateProductList(values);

                            if (allRequiredFieldsFilled(updatedProductList)) {
                              setProductList(updatedProductList);
                              submitForm();
                            }
                          }}>
                          Save
                        </Button>
                      </>
                    ) : (
                      <Button
                        type="button"
                        variant="secondary"
                        fullWidth={false}
                        onClick={event => {
                          event.preventDefault();
                          setIsEditable(true);
                          setDisableButton(true);
                          setMainAssetClass(
                            normalizeAssetClassStr(serialNumbers?.serial_numbers[0].serial_number_asset_class),
                          );
                        }}>
                        Edit
                      </Button>
                    )
                  ) : (
                    <></>
                  )}
                </div>
              </Form>
            );
          }}
        </Formik>
      </div>
      {showFinancing && (
        <>
          <LineBreak bordered />
          <h4>{formatMessage(messages.financingTitle)}</h4>
          <p>{formatMessage(messages.financingDescription)}</p>
          <div className="offer-card--container">
            <OfferCard
              img={
                dealerFlowTemplate?.promotional_logo
                  ? returnImgSrc(dealerFlowTemplate.promotional_logo)
                  : OEMType === 'ezgo'
                    ? EzGoLogo
                    : AquaLogo
              }
              title={'Financing offer'}
              offer={USDollar.format(approvedAmount || 0)}
            />
          </div>
          {showConfirmButton ? (
            <Button
              type="button"
              variant="secondary"
              className="full-width"
              testId="button-add-accessory"
              disabled={disableButton}
              onClick={() => {
                if (allRequiredFieldsFilled(productList)) {
                  onSubmit && onSubmit(productList);
                }
              }}>
              {ButtonText ? ButtonText : formatMessage(messages.financingConfirm)}
            </Button>
          ) : (
            <LineBreak bordered />
          )}
          <p className="financial-disclaimer">{formatMessage(messages.financingConfirmDisclaimer)}</p>
        </>
      )}
    </div>
  );
};

export default ProductSummary;
